import {
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { X } from 'lucide-react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslations } from '@/i18n';
import { addDays, format, isAfter, isBefore, parseISO, startOfDay, subDays } from 'date-fns';
import { ka } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { useRouter } from '@/lib/navigation';
import { useCreateTrip } from '@/hooks/trips/useCreateTrip';
import { useUpdateTripForm } from '@/hooks/trips/useUpdateTripForm';
import { useEmployees } from '@/hooks/useEmployees';
import { useSubstituteEmployees, type SubstituteEmployee } from '@/hooks/useSubstituteEmployees';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useCountries } from '@/hooks/useCountries';
import { useCities } from '@/hooks/useCities';
import { useRoamingQuote } from '@/hooks/trips/useRoamingQuote';
import { useDailyPayoutQuote } from '@/hooks/trips/useDailyPayoutQuote';
import { useCostCentre } from '@/hooks/trips/useCostCentre';
import { LOCAL_TRIP_COUNTRY_ID } from '@/config/trips';
import type { TripDetail, TripUser } from '@/hooks/trips/types';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  useSelectSearch,
  VirtualizedSelectContent,
  type VirtualizedSelectOption,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';

interface TripFormProps {
  mode?: 'create' | 'edit';
  tripId?: string;
  tripNumber?: string;
  initialData?: Partial<TripFormValues>;
  onSubmit?: (data: TripFormValues) => Promise<void>;
  currentUser?: { id: string; email: string; name: string };
  tripAuthor?: TripUser;
  derivedMode?: 'extend' | 'shorten' | null;
  parentTripId?: string | null;
  parentTrip?: TripDetail | null;
  parentRequestNumber?: string | null;
  /** Pre-loaded acting employee name from trip data (used as fallback before list is fetched) */
  actingEmployeeName?: string;
}

export type TripFormValues = {
  tripName: string;
  tripType: 'local' | 'international';
  initiator: string;
  actingEmployee: string;
  permanentResidenceCountry: string;
  permanentResidenceCity: string;
  startDate: Date | null;
  endDate: Date | null;
  purpose: string;
  additionalComment?: string;
  roamingExpense: boolean;
  roamingAmount: string;
  roamingPhoneNumber: string;
  accommodationExpense: boolean;
  dailyPayoutAmount: string;
};

// Trip name is now loaded from translations - see useTripNameDefault hook below

const DEFAULT_CURRENCY_CODE = 'GEL';
const ROAMING_CURRENCY_KEYS = [
  'currency_code',
  'currency',
  'currency_label',
  'currency_name',
  'roaming_currency_code',
  'roaming_currency_label',
  'roaming_currency',
] as const;
const DAILY_CURRENCY_KEYS = [
  'currency_code',
  'currency',
  'currency_label',
  'currency_name',
  'daily_currency_code',
  'daily_currency_label',
] as const;

function extractCurrencyCode(
  record: Record<string, unknown> | null | undefined,
  candidates: readonly string[]
): string | undefined {
  if (!record) {
    return undefined;
  }

  for (const key of candidates) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim();
    }
  }

  return undefined;
}

function normalizeAmount(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const normalized = value
      .replace(/[^0-9,.-]/g, '')
      .replace(',', '.')
      .trim();
    if (!normalized) {
      return null;
    }
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function formatCurrencyDisplay(amount: unknown, currencyCode?: string | null): string {
  const numericAmount = normalizeAmount(amount);

  if (numericAmount == null) {
    return typeof amount === 'string' ? amount : '';
  }

  if (currencyCode && currencyCode.trim()) {
    try {
      return new Intl.NumberFormat('ka-GE', {
        style: 'currency',
        currency: currencyCode,
      }).format(numericAmount);
    } catch {
      return `${numericAmount} ${currencyCode}`;
    }
  }

  try {
    return new Intl.NumberFormat('ka-GE', {
      style: 'currency',
      currency: DEFAULT_CURRENCY_CODE,
    }).format(numericAmount);
  } catch {
    return numericAmount.toString();
  }
}

type TranslateFn = ReturnType<typeof useTranslations>;

interface TripExpensesSectionProps {
  form: UseFormReturn<TripFormValues>;
  tExpenses: TranslateFn;
  tCommon: TranslateFn;
}

const TripExpensesSection = memo(function TripExpensesSection({
  form,
  tExpenses,
  tCommon,
}: TripExpensesSectionProps) {
  const control = form.control;
  const setValue = form.setValue;
  const getValues = form.getValues;

  const [
    roamingExpense = false,
    accommodationExpense = true,
    initiatorValue = '',
    startDateValue,
    endDateValue,
    tripTypeValue = 'local',
  ] = useWatch({
    control,
    name: [
      'roamingExpense',
      'accommodationExpense',
      'initiator',
      'startDate',
      'endDate',
      'tripType',
    ],
    exact: true,
  }) as [
    boolean | undefined,
    boolean | undefined,
    string | undefined,
    Date | null | undefined,
    Date | null | undefined,
    'local' | 'international' | undefined,
  ];

  const startDate =
    startDateValue instanceof Date
      ? startDateValue
      : startDateValue
        ? new Date(startDateValue)
        : null;
  const endDate =
    endDateValue instanceof Date ? endDateValue : endDateValue ? new Date(endDateValue) : null;

  const initiatorId = Number.parseInt(initiatorValue ?? '', 10);
  const hasInitiator = Number.isFinite(initiatorId);

  const startDateString = startDate ? format(startDate, 'yyyy-MM-dd') : null;
  const endDateString = endDate ? format(endDate, 'yyyy-MM-dd') : null;
  const effectiveTripType = tripTypeValue ?? 'local';
  const isInternational = effectiveTripType === 'international';

  const shouldFetchRoaming = Boolean(
    isInternational && roamingExpense && hasInitiator && startDateString && endDateString
  );
  const shouldFetchDaily = Boolean(
    accommodationExpense &&
      hasInitiator &&
      startDateString &&
      endDateString &&
      effectiveTripType !== undefined
  );

  const roamingCheckboxId = useId();
  const accommodationCheckboxId = useId();

  const {
    data: roamingData,
    isError: isRoamingError,
    error: roamingError,
    isFetching: isRoamingFetching,
  } = useRoamingQuote({
    initiatorId: hasInitiator ? initiatorId : undefined,
    startDate: startDateString,
    endDate: endDateString,
    enabled: shouldFetchRoaming,
  });

  const {
    data: dailyData,
    isError: isDailyError,
    error: dailyError,
    isFetching: isDailyFetching,
  } = useDailyPayoutQuote({
    initiatorId: hasInitiator ? initiatorId : undefined,
    tripType: effectiveTripType,
    startDate: startDateString,
    endDate: endDateString,
    enabled: shouldFetchDaily,
  });

  const roamingErrorMessage =
    roamingExpense && shouldFetchRoaming && isRoamingError ? (roamingError?.message ?? '') : '';

  const dailyErrorMessage =
    accommodationExpense && shouldFetchDaily && isDailyError ? (dailyError?.message ?? '') : '';

  useEffect(() => {
    if (!isInternational || !roamingExpense) {
      if (getValues('roamingAmount')) {
        setValue('roamingAmount', '', { shouldDirty: false, shouldValidate: false });
      }
      if (getValues('roamingPhoneNumber')) {
        setValue('roamingPhoneNumber', '', { shouldDirty: false, shouldValidate: false });
      }
      return;
    }

    if (!shouldFetchRoaming || isRoamingError) {
      if (getValues('roamingAmount')) {
        setValue('roamingAmount', '', { shouldDirty: false, shouldValidate: false });
      }
      if (getValues('roamingPhoneNumber')) {
        setValue('roamingPhoneNumber', '', { shouldDirty: false, shouldValidate: false });
      }
      return;
    }

    if (isRoamingFetching) {
      if (getValues('roamingAmount')) {
        setValue('roamingAmount', '', { shouldDirty: false, shouldValidate: false });
      }
      if (getValues('roamingPhoneNumber')) {
        setValue('roamingPhoneNumber', '', { shouldDirty: false, shouldValidate: false });
      }
      return;
    }

    if (roamingData) {
      const currencyCode = extractCurrencyCode(roamingData, ROAMING_CURRENCY_KEYS);
      const formattedAmount = formatCurrencyDisplay(roamingData.roaming_limit_amount, currencyCode);
      const phoneNumber =
        typeof roamingData.roaming_mobile_number === 'string'
          ? roamingData.roaming_mobile_number
          : '';

      if (getValues('roamingAmount') !== (formattedAmount ?? '')) {
        setValue('roamingAmount', formattedAmount ?? '', {
          shouldDirty: false,
          shouldValidate: false,
        });
      }

      if (getValues('roamingPhoneNumber') !== phoneNumber) {
        setValue('roamingPhoneNumber', phoneNumber, {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    }
  }, [
    accommodationExpense,
    effectiveTripType,
    getValues,
    isInternational,
    isRoamingError,
    isRoamingFetching,
    roamingData,
    roamingExpense,
    setValue,
    shouldFetchRoaming,
  ]);

  useEffect(() => {
    if (!accommodationExpense) {
      if (getValues('dailyPayoutAmount')) {
        setValue('dailyPayoutAmount', '', { shouldDirty: false, shouldValidate: false });
      }
      return;
    }

    if (!shouldFetchDaily || isDailyError) {
      if (getValues('dailyPayoutAmount')) {
        setValue('dailyPayoutAmount', '', { shouldDirty: false, shouldValidate: false });
      }
      return;
    }

    if (isDailyFetching) {
      if (getValues('dailyPayoutAmount')) {
        setValue('dailyPayoutAmount', '', { shouldDirty: false, shouldValidate: false });
      }
      return;
    }

    if (dailyData) {
      const currencyCode = extractCurrencyCode(dailyData, DAILY_CURRENCY_KEYS);
      const formattedAmount = formatCurrencyDisplay(dailyData.daily_payout_amount, currencyCode);

      if (getValues('dailyPayoutAmount') !== (formattedAmount ?? '')) {
        setValue('dailyPayoutAmount', formattedAmount ?? '', {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
    }
  }, [
    accommodationExpense,
    dailyData,
    getValues,
    isDailyError,
    isDailyFetching,
    setValue,
    shouldFetchDaily,
  ]);

  return (
    <div className="space-y-[0.75rem]">
      {isInternational && (
        <div className="space-y-3">
          <FormField
            control={control}
            name="roamingExpense"
            render={({ field }) => (
              <FormItem className="grid gap-[0.4rem]">
                <div className="flex min-h-[2.25rem] items-center gap-1.5">
                  <FormControl>
                    <Checkbox
                      id={roamingCheckboxId}
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      className="h-5 w-5 rounded border-2 data-[state=checked]:border-[#242424] data-[state=checked]:bg-[#242424]"
                    />
                  </FormControl>
                  <label
                    htmlFor={roamingCheckboxId}
                    className="cursor-pointer text-[15px] leading-6 text-[#292929] select-none"
                  >
                    {tExpenses('roaming')}
                  </label>
                </div>
              </FormItem>
            )}
          />

          {roamingExpense && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormField
                  control={control}
                  name="roamingAmount"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="px-1 text-sm font-medium text-[#292929]">
                        {tExpenses('roamingAmount')}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <input
                            {...field}
                            value={field.value ?? ''}
                            readOnly
                            tabIndex={-1}
                            aria-readonly="true"
                            onFocus={(event) => event.currentTarget.blur()}
                            className="h-14 w-full cursor-default rounded-2xl border border-[#ebebeb] bg-[#f8f8f8] px-4 py-3 text-base text-[#292929] outline-none placeholder:text-[#bbbbbb]"
                            placeholder={isRoamingFetching ? tCommon('loading') : ''}
                          />
                          {isRoamingFetching && (
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                              <Spinner variant="circle" className="h-4 w-4 text-[#999999]" />
                            </div>
                          )}
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="roamingPhoneNumber"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="px-1 text-sm font-medium text-[#292929]">
                        {tExpenses('phoneNumber')}
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <input
                            {...field}
                            value={field.value ?? ''}
                            readOnly
                            tabIndex={-1}
                            aria-readonly="true"
                            onFocus={(event) => event.currentTarget.blur()}
                            className="h-14 w-full cursor-default rounded-2xl border border-[#ebebeb] bg-[#f8f8f8] px-4 py-3 text-base text-[#292929] outline-none placeholder:text-[#bbbbbb]"
                            placeholder={isRoamingFetching ? tCommon('loading') : ''}
                          />
                          {isRoamingFetching && (
                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                              <Spinner variant="circle" className="h-4 w-4 text-[#999999]" />
                            </div>
                          )}
                        </div>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              {roamingErrorMessage ? (
                <p className="px-1 text-sm text-red-500">{roamingErrorMessage}</p>
              ) : null}
            </>
          )}
        </div>
      )}

      <div className="space-y-3 rounded-2xl bg-white">
        <FormField
          control={control}
          name="accommodationExpense"
          render={({ field }) => (
            <FormItem className="grid gap-[0.4rem]">
              <div className="flex min-h-[2.25rem] items-center gap-1.5">
                <FormControl>
                  <Checkbox
                    id={accommodationCheckboxId}
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    className="h-5 w-5 rounded border-2 data-[state=checked]:border-[#242424] data-[state=checked]:bg-[#242424]"
                  />
                </FormControl>
                <label
                  htmlFor={accommodationCheckboxId}
                  className="cursor-pointer text-[15px] leading-6 text-[#292929] select-none"
                >
                  {tExpenses('accommodation')}
                </label>
              </div>
            </FormItem>
          )}
        />

        {accommodationExpense && (
          <>
            <FormField
              control={control}
              name="dailyPayoutAmount"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="px-1 text-sm font-medium text-[#292929]">
                    {tExpenses('accommodationAmount')}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <input
                        {...field}
                        value={field.value ?? ''}
                        readOnly
                        tabIndex={-1}
                        aria-readonly="true"
                        onFocus={(event) => event.currentTarget.blur()}
                        className="h-14 w-full cursor-default rounded-2xl border border-[#ebebeb] bg-[#f8f8f8] px-4 py-3 text-base text-[#292929] outline-none placeholder:text-[#bbbbbb]"
                        placeholder={isDailyFetching ? tCommon('loading') : ''}
                      />
                      {isDailyFetching && (
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                          <Spinner variant="circle" className="h-4 w-4 text-[#999999]" />
                        </div>
                      )}
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />
            {dailyErrorMessage ? (
              <p className="px-1 text-sm text-red-500">{dailyErrorMessage}</p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
});

function FloatingSelectValue({
  label,
  hasValue,
  placeholder,
  children,
  disabled = false,
}: {
  label: string;
  hasValue: boolean;
  placeholder?: string;
  children?: ReactNode;
  disabled?: boolean;
}) {
  const { searchTerm } = useSelectSearch();
  const isTyping = searchTerm.length > 0;
  const showValue = hasValue || isTyping;
  const labelText = showValue ? label : (placeholder ?? label);
  const displayText = isTyping ? searchTerm : null;

  return (
    <div className="relative w-full">
      <div
        className={cn(
          'pointer-events-none absolute left-0 origin-left transition-all duration-200',
          disabled ? 'text-[#bbbbbb]' : 'text-[#666666]',
          showValue
            ? 'top-0 text-[0.8125rem] leading-4'
            : 'top-1/2 -translate-y-1/2 text-base leading-6'
        )}
      >
        {labelText}
      </div>
      {showValue && (
        <div
          className={cn(
            'flex w-full min-w-0 items-start pt-5 text-base leading-6',
            disabled ? 'text-[#bbbbbb]' : 'text-[#292929]'
          )}
        >
          {isTyping ? (
            <span className="truncate leading-6">{displayText}</span>
          ) : (
            <span className="truncate">
              <SelectValue placeholder={placeholder ?? label}>{children}</SelectValue>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export function TripForm({
  mode = 'create',
  tripId,
  initialData,
  onSubmit,
  currentUser,
  tripAuthor,
  derivedMode = null,
  parentTripId = null,
  parentTrip = null,
  parentRequestNumber = null,
  actingEmployeeName: initialActingEmployeeName,
}: TripFormProps) {
  const t = useTranslations('tripForm');
  const tFields = useTranslations('tripForm.fields');
  const tExpenses = useTranslations('tripForm.expenses');
  const tActions = useTranslations('trips.actions');
  const tCommon = useTranslations('common');
  const tValidation = useTranslations('validation');

  const tTrips = useTranslations('trips');

  // Default trip name from translations
  const TRIP_NAME_DEFAULT = t('defaultTripName');

  const createTripMutation = useCreateTrip();
  const updateTripMutation = useUpdateTripForm();
  const { data: employees, isLoading: employeesLoading, error: employeesError } = useEmployees();
  const { data: countries, isLoading: countriesLoading, error: countriesError } = useCountries();

  // State for substitute employee occupied days confirmation dialog
  const [showSubstituteWarningDialog, setShowSubstituteWarningDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<TripFormValues | null>(null);

  const initialTripType = initialData?.tripType ?? 'local';
  const [tripType, setTripType] = useState<'local' | 'international'>(initialTripType);
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);
  const [isEndDateOpen, setIsEndDateOpen] = useState(false);
  // Track which dates have been approved for fetching (set when dropdown opens)
  // This prevents auto-fetching on date changes - only fetch when user clicks dropdown
  const [approvedFetchDates, setApprovedFetchDates] = useState<{
    start: string | null;
    end: string | null;
  } | null>(null);
  const parentStartDate = useMemo(() => {
    if (!parentTrip?.startDate) return null;
    const parsed = parseISO(parentTrip.startDate);
    return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
  }, [parentTrip?.startDate]);

  const parentEndDate = useMemo(() => {
    if (!parentTrip?.endDate) return null;
    const parsed = parseISO(parentTrip.endDate);
    return Number.isNaN(parsed.getTime()) ? null : startOfDay(parsed);
  }, [parentTrip?.endDate]);
  const hasParent = Boolean(parentTripId);
  const isDerivedShorten = hasParent && derivedMode === 'shorten';
  const isDerivedExtend = hasParent && derivedMode === 'extend';
  const isInitiatorLocked = hasParent;
  const isTripTypeLocked = isDerivedShorten;
  const isStartDateLocked = isDerivedShorten || isDerivedExtend;
  const parentNumberDisplay =
    parentRequestNumber ?? parentTrip?.requestNumber ?? parentTrip?.number ?? undefined;

  // Create schema with translated messages - all fields required except additionalComment and actingEmployee
  const tripFormSchema = useMemo(
    () =>
      z
        .object({
          tripName: z.string().min(1, tValidation('required')),
          tripType: z.enum(['local', 'international']),
          initiator: z.string().min(1, tValidation('required')),
          actingEmployee: z.string(),
          permanentResidenceCountry: z.string().min(1, tValidation('required')),
          permanentResidenceCity: z.string().min(1, tValidation('required')),
          startDate: z
            .date({
              message: tValidation('required'),
            })
            .nullable(),
          endDate: z
            .date({
              message: tValidation('required'),
            })
            .nullable(),
          purpose: z.string().min(1, tValidation('required')),
          additionalComment: z.string().optional(),
          roamingExpense: z.boolean(),
          roamingAmount: z.string(),
          roamingPhoneNumber: z.string(),
          accommodationExpense: z.boolean(),
          dailyPayoutAmount: z.string(),
        })
        .superRefine((data, ctx) => {
          if (!data.startDate) {
            ctx.addIssue({
              code: 'custom',
              path: ['startDate'],
              message: tValidation('required'),
            });
          }

          if (!data.endDate) {
            ctx.addIssue({
              code: 'custom',
              path: ['endDate'],
              message: tValidation('required'),
            });
          }

          if (data.startDate && data.endDate && data.endDate < data.startDate) {
            ctx.addIssue({
              code: 'custom',
              path: ['endDate'],
              message: tValidation('dateOrder') || 'End date must be after start date',
            });
          }

          if (isDerivedShorten && data.endDate && parentEndDate) {
            const latestAllowed = subDays(parentEndDate, 1);
            if (isAfter(data.endDate, latestAllowed)) {
              ctx.addIssue({
                code: 'custom',
                path: ['endDate'],
                message:
                  tValidation('dateOrder') || 'End date must be before the parent trip end date',
              });
            }
          }

          if (isDerivedExtend && data.endDate && parentEndDate) {
            const earliestAllowed = addDays(parentEndDate, 1);
            if (isBefore(data.endDate, earliestAllowed)) {
              ctx.addIssue({
                code: 'custom',
                path: ['endDate'],
                message:
                  tValidation('dateOrder') || 'End date must be after the parent trip end date',
              });
            }
          }
        }),
    [isDerivedExtend, isDerivedShorten, parentEndDate, tValidation]
  );

  const defaultValues = useMemo<TripFormValues>(() => {
    const normalizedTripType = initialTripType;
    const initialResidenceCountry =
      typeof initialData?.permanentResidenceCountry === 'string' &&
      initialData.permanentResidenceCountry.trim().length > 0
        ? initialData.permanentResidenceCountry
        : normalizedTripType === 'local'
          ? LOCAL_TRIP_COUNTRY_ID
          : '';

    return {
      tripName: TRIP_NAME_DEFAULT,
      tripType: normalizedTripType,
      initiator: initialData?.initiator ?? '',
      actingEmployee: initialData?.actingEmployee ?? '',
      permanentResidenceCountry: initialResidenceCountry,
      permanentResidenceCity: initialData?.permanentResidenceCity ?? '',
      startDate: initialData?.startDate ?? null,
      endDate: initialData?.endDate ?? null,
      purpose: initialData?.purpose ?? '',
      additionalComment: initialData?.additionalComment ?? '',
      roamingExpense: initialData?.roamingExpense ?? false,
      roamingAmount: initialData?.roamingAmount ?? '',
      roamingPhoneNumber: initialData?.roamingPhoneNumber ?? '',
      accommodationExpense: initialData?.accommodationExpense ?? true,
      dailyPayoutAmount: initialData?.dailyPayoutAmount ?? '',
    };
  }, [initialData, initialTripType, TRIP_NAME_DEFAULT]);

  const form = useForm<TripFormValues>({
    resolver: zodResolver(tripFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!hasParent) {
      return;
    }

    if (isDerivedShorten && parentStartDate) {
      form.setValue('startDate', parentStartDate, { shouldDirty: false, shouldValidate: false });
    }
  }, [form, hasParent, isDerivedShorten, parentStartDate]);

  const initiatorValue = useWatch({ control: form.control, name: 'initiator' });
  const initiatorId = useMemo(() => {
    const parsed = Number.parseInt(initiatorValue ?? '', 10);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [initiatorValue]);

  const { data: costCentreData } = useCostCentre({
    initiatorId,
    enabled: Boolean(initiatorId),
  });

  const costCentreValue = useMemo(() => {
    const rawValue = costCentreData?.cost_centre;
    if (!rawValue || typeof rawValue !== 'string') return '';
    const trimmed = rawValue.trim();
    return trimmed.toLowerCase() === 'false' ? '' : trimmed;
  }, [costCentreData]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const startDateValue = useWatch({ control: form.control, name: 'startDate' });
  const endDateValue = useWatch({ control: form.control, name: 'endDate' });
  const actingEmployeeValue = useWatch({ control: form.control, name: 'actingEmployee' });

  // Fetch substitute employees with availability data based on trip dates
  const startDateString = startDateValue ? format(startDateValue, 'yyyy-MM-dd') : null;
  const endDateString = endDateValue ? format(endDateValue, 'yyyy-MM-dd') : null;

  // Only enable fetch when dropdown has been opened for the current dates
  // This prevents auto-fetching on date changes - user must click dropdown to fetch
  const shouldFetchSubstitutes =
    approvedFetchDates !== null &&
    approvedFetchDates.start === startDateString &&
    approvedFetchDates.end === endDateString;

  const {
    data: substituteEmployees,
    isLoading: substituteEmployeesLoading,
    error: substituteEmployeesError,
  } = useSubstituteEmployees({
    startDate: startDateString,
    endDate: endDateString,
    enabled: shouldFetchSubstitutes,
  });

  // Get the selected substitute employee's availability info
  const selectedSubstituteEmployee = useMemo<SubstituteEmployee | null>(() => {
    if (!actingEmployeeValue || !substituteEmployees) return null;
    return substituteEmployees.find((emp) => emp.id === actingEmployeeValue) ?? null;
  }, [actingEmployeeValue, substituteEmployees]);

  // Check if selected substitute has occupied days
  const substituteHasOccupiedDays =
    selectedSubstituteEmployee?.occupiedDaysCount != null &&
    selectedSubstituteEmployee.occupiedDaysCount > 0;

  // Format occupied date ranges for display
  const formatOccupiedRanges = useCallback((employee: SubstituteEmployee | null): string => {
    if (!employee?.occupiedRanges || employee.occupiedRanges.length === 0) return '';

    return employee.occupiedRanges
      .map((range) => {
        const start = parseISO(range.startDate);
        const end = parseISO(range.endDate);
        if (range.startDate === range.endDate) {
          return format(start, 'dd/MM/yyyy');
        }
        return `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
      })
      .join(', ');
  }, []);
  const normalizedStartDate = useMemo(
    () => (startDateValue ? startOfDay(startDateValue) : null),
    [startDateValue]
  );
  const disableStartDates = useMemo(
    () => (date: Date) => {
      if (isStartDateLocked) {
        return true;
      }
      return isBefore(startOfDay(date), today);
    },
    [isStartDateLocked, today]
  );
  const disableEndDates = useMemo(
    () => (date: Date) => {
      const day = startOfDay(date);

      if (!hasParent && isBefore(day, today)) {
        return true;
      }
      if (normalizedStartDate && isBefore(day, normalizedStartDate)) {
        return true;
      }
      if (isDerivedShorten && parentEndDate) {
        const latestAllowed = subDays(parentEndDate, 1);
        if (isAfter(day, latestAllowed)) {
          return true;
        }
      }
      if (isDerivedExtend && parentEndDate) {
        const earliestAllowed = addDays(parentEndDate, 1);
        if (isBefore(day, earliestAllowed)) {
          return true;
        }
      }

      return false;
    },
    [hasParent, isDerivedExtend, isDerivedShorten, normalizedStartDate, parentEndDate, today]
  );

  const handleTripTypeChange = (nextType: 'local' | 'international') => {
    if (isTripTypeLocked) {
      return;
    }
    if (tripType === nextType) {
      return;
    }

    setTripType(nextType);
    form.setValue('tripType', nextType, {
      shouldDirty: true,
      shouldTouch: true,
    });

    if (nextType === 'local') {
      form.setValue('permanentResidenceCountry', LOCAL_TRIP_COUNTRY_ID, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    }
  };

  const tripNameFieldRef = useRef<HTMLTextAreaElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const errorBannerRef = useRef<HTMLDivElement | null>(null);
  // Floating labels handled via peer CSS; no focus state needed

  useEffect(() => {
    if (form.getValues('tripName') !== TRIP_NAME_DEFAULT) {
      form.setValue('tripName', TRIP_NAME_DEFAULT, { shouldDirty: false, shouldValidate: false });
    }
    const element = tripNameFieldRef.current;
    if (element) {
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    }
  }, [form, TRIP_NAME_DEFAULT]);

  // Actual form submission logic (called directly or after confirmation)
  const executeSubmit = useCallback(
    async (data: TripFormValues) => {
      if (onSubmit) {
        await onSubmit(data);
      } else if (mode === 'edit' && tripId) {
        // Use the update mutation for edit mode
        updateTripMutation.mutate({ id: tripId, data });
      } else {
        // Use the create mutation for create mode
        createTripMutation.mutate(data);
      }
    },
    [createTripMutation, mode, onSubmit, tripId, updateTripMutation]
  );

  const handleSubmit = async (data: TripFormValues) => {
    // Check if selected substitute employee has occupied days
    if (substituteHasOccupiedDays && selectedSubstituteEmployee) {
      // Store the form data and show confirmation dialog
      setPendingFormData(data);
      setShowSubstituteWarningDialog(true);
      return;
    }

    // No warning needed, proceed with submission
    await executeSubmit(data);
  };

  // Handler for confirming submission despite substitute having occupied days
  const handleConfirmSubstituteWarning = useCallback(() => {
    setShowSubstituteWarningDialog(false);
    if (pendingFormData) {
      executeSubmit(pendingFormData);
      setPendingFormData(null);
    }
  }, [executeSubmit, pendingFormData]);

  // Handler for canceling the substitute warning dialog
  const handleCancelSubstituteWarning = useCallback(() => {
    setShowSubstituteWarningDialog(false);
    setPendingFormData(null);
  }, []);

  // Check if any mutation is pending
  const isSubmitting =
    createTripMutation.isPending || updateTripMutation.isPending || form.formState.isSubmitting;
  const isBusy = isSubmitting;

  const router = useRouter();
  const selectedCountryId = form.watch('permanentResidenceCountry');
  const isResidenceCityDisabled = !selectedCountryId;
  const previousCountryRef = useRef<string | null>(selectedCountryId ?? null);
  const {
    data: residenceCities,
    isLoading: citiesLoading,
    error: citiesError,
  } = useCities(selectedCountryId || undefined);

  // Convert residence cities to virtualized options format for large lists
  const residenceCityOptions = useMemo<VirtualizedSelectOption[]>(
    () =>
      (residenceCities ?? []).map((city) => ({
        value: city.id,
        label: city.name,
        searchText: city.name,
      })),
    [residenceCities]
  );

  // Convert employees to virtualized options format for large lists
  const employeeOptions = useMemo<VirtualizedSelectOption[]>(
    () =>
      (employees ?? []).map((employee) => ({
        value: employee.id,
        label: employee.name,
        searchText: employee.name,
      })),
    [employees]
  );

  // Convert countries to virtualized options format for large lists
  const countryOptions = useMemo<VirtualizedSelectOption[]>(
    () =>
      (countries ?? []).map((country) => ({
        value: country.id,
        label: country.name,
        searchText: country.name,
      })),
    [countries]
  );

  useEffect(() => {
    const previousCountry = previousCountryRef.current;
    if (selectedCountryId === previousCountry) {
      return;
    }

    const currentCity = form.getValues('permanentResidenceCity');

    if (!selectedCountryId) {
      if (currentCity) {
        form.setValue('permanentResidenceCity', '', {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    } else if (previousCountry && previousCountry !== selectedCountryId) {
      if (currentCity) {
        form.setValue('permanentResidenceCity', '', {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }

    previousCountryRef.current = selectedCountryId ?? null;
  }, [selectedCountryId, form]);

  useEffect(() => {
    if (!selectedCountryId || !residenceCities || residenceCities.length === 0) {
      return;
    }

    const currentCityId = form.getValues('permanentResidenceCity');
    if (!currentCityId) {
      return;
    }

    const existsInOptions = residenceCities.some((city) => city.id === currentCityId);
    if (!existsInOptions) {
      form.setValue('permanentResidenceCity', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [residenceCities, selectedCountryId, form]);

  const scrollIntoViewWithOffset = useCallback((element: HTMLElement | null) => {
    if (!element || typeof window === 'undefined') {
      return;
    }
    const offset = window.innerWidth >= 1024 ? 120 : 90;
    const target = element.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({
      top: Math.max(0, target),
      behavior: 'smooth',
    });
  }, []);

  const handleCancel = () => {
    form.reset();
    if (mode === 'edit' && tripId) {
      // In edit mode, go back to trip details
      router.push(`/trips/${tripId}`);
    } else {
      // In create mode, go to homepage
      router.push('/');
    }
  };

  // Generate initials from author name
  // In edit mode, show the trip's actual author; in create mode, show the current user
  const authorName =
    mode === 'edit' && tripAuthor?.name
      ? tripAuthor.name
      : currentUser?.name || currentUser?.email || t('defaultUser');
  const displayInitials = authorName
    .split(' ')
    .map((word: string) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const { errors, isSubmitted, isSubmitting: formStateSubmitting } = form.formState;

  useEffect(() => {
    if (!isSubmitted || formStateSubmitting) {
      return;
    }

    const hasErrors = Object.keys(errors).length > 0;
    if (!hasErrors) {
      return;
    }

    const formElement = formRef.current;
    if (!formElement) {
      return;
    }

    requestAnimationFrame(() => {
      const firstInvalidElement = formElement.querySelector<HTMLElement>('[aria-invalid="true"]');

      if (firstInvalidElement) {
        scrollIntoViewWithOffset(firstInvalidElement);
        if (typeof firstInvalidElement.focus === 'function') {
          firstInvalidElement.focus({ preventScroll: true });
        } else {
          const focusableChild = firstInvalidElement.querySelector<HTMLElement>(
            'input, textarea, button, [tabindex]:not([tabindex="-1"])'
          );
          focusableChild?.focus?.({ preventScroll: true });
        }
        return;
      }

      scrollIntoViewWithOffset(formElement);
    });
  }, [errors, isSubmitted, formStateSubmitting, scrollIntoViewWithOffset]);

  useEffect(() => {
    if (
      (createTripMutation.isError && createTripMutation.error) ||
      (updateTripMutation.isError && updateTripMutation.error)
    ) {
      requestAnimationFrame(() => {
        scrollIntoViewWithOffset(errorBannerRef.current);
      });
    }
  }, [
    createTripMutation.isError,
    createTripMutation.error,
    updateTripMutation.isError,
    updateTripMutation.error,
    scrollIntoViewWithOffset,
  ]);

  return (
    <div className="w-full rounded-3xl border border-[#f1f1f1] bg-white p-4 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)] sm:p-5">
      <Form {...form}>
        <form
          id="trip-form"
          ref={formRef}
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col gap-4"
        >
          {(employeesError || createTripMutation.isError || updateTripMutation.isError) && (
            <div
              ref={errorBannerRef}
              className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600"
            >
              {employeesError && <p>{employeesError.message}</p>}
              {createTripMutation.isError && createTripMutation.error && (
                <p>{createTripMutation.error.message}</p>
              )}
              {updateTripMutation.isError && updateTripMutation.error && (
                <p>{updateTripMutation.error.message}</p>
              )}
            </div>
          )}

          {/* Author Info Section */}
          <div className="flex items-center gap-3 rounded-2xl bg-[#f9f9f9] px-4 py-4 sm:px-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white">
              <span className="text-sm font-medium text-[#292929]">{displayInitials}</span>
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="text-[13px] leading-4 text-[#666666]">{t('author')}</span>
              <span className="text-base leading-6 text-[#292929]">{authorName}</span>
            </div>
          </div>

          {/* Main Form Fields */}
          <div className="space-y-4">
            {/* Trip Name with floating label */}
            <FormField
              control={form.control}
              name="tripName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="tripName" className="px-1 text-sm font-medium text-[#666666]">
                    {tFields('tripName')}
                  </FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      id="tripName"
                      ref={(node) => {
                        field.ref(node);
                        tripNameFieldRef.current = node;
                      }}
                      value={TRIP_NAME_DEFAULT}
                      readOnly
                      tabIndex={-1}
                      aria-readonly="true"
                      rows={2}
                      placeholder=""
                      onFocus={(event) => event.currentTarget.blur()}
                      className="min-h-[5rem] w-full resize-none rounded-2xl border border-[#f1f1f1] bg-[#f7f7f7] px-4 py-3 text-[15px] leading-6 whitespace-pre-wrap text-[#515151] outline-none sm:min-h-[5.5rem]"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {hasParent && parentNumberDisplay ? (
              <div className="flex flex-col gap-2">
                <label className="px-1 text-sm font-medium text-[#292929]">{t('parentTrip')}</label>
                <button
                  type="button"
                  onClick={() => {
                    if (parentTripId) {
                      router.push(`/trips/${parentTripId}`);
                    }
                  }}
                  className="flex h-12 items-center justify-between rounded-2xl border border-[#e4e4e4] bg-[#f9f9f9] px-4 py-2 text-left text-base text-[#292929] transition hover:border-[#d0d0d0]"
                >
                  <span className="font-medium">{parentNumberDisplay}</span>
                  <img
                    src="/assets/icons/chevron-left.svg"
                    alt=""
                    className="h-5 w-5 rotate-180 transform"
                  />
                </button>
              </div>
            ) : null}
            {/* Parent trip link (derived) */}

            {/* Trip Type Toggle */}
            <div className="flex flex-col items-start gap-3 self-stretch">
              <label className="px-1 text-sm font-medium text-[#292929]">
                {tFields('tripType')}
              </label>
              <div className="flex flex-col gap-3 self-stretch sm:flex-row">
                <button
                  type="button"
                  disabled={isTripTypeLocked}
                  onClick={() => {
                    handleTripTypeChange('local');
                  }}
                  className={cn(
                    'flex h-14 w-full flex-1 items-center gap-3 rounded-2xl border border-[#ebebeb] px-3.5 py-2.5 transition-all',
                    isTripTypeLocked
                      ? 'cursor-not-allowed bg-[#f9f9f9] opacity-70'
                      : 'cursor-pointer'
                  )}
                >
                  <div className="rounded-xl bg-[#f1f1f1] p-1.5">
                    <img src="/assets/icons/home.svg" alt="" className="h-6 w-6" />
                  </div>
                  <span className="flex-1 text-left text-[15px] text-black">
                    {tFields('local')}
                  </span>
                  <div
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full border',
                      tripType === 'local' ? 'border-[#242424] bg-[#242424]' : 'border-[#f1f1f1]'
                    )}
                  >
                    {tripType === 'local' && <div className="h-2.5 w-2.5 rounded-full bg-white" />}
                  </div>
                </button>

                <button
                  type="button"
                  disabled={isTripTypeLocked}
                  onClick={() => {
                    handleTripTypeChange('international');
                  }}
                  className={cn(
                    'flex h-14 w-full flex-1 items-center gap-3 rounded-2xl border border-[#ebebeb] px-3.5 py-2.5 transition-all',
                    isTripTypeLocked
                      ? 'cursor-not-allowed bg-[#f9f9f9] opacity-70'
                      : 'cursor-pointer'
                  )}
                >
                  <div className="rounded-xl bg-[#f1f1f1] p-1.5">
                    <img src="/assets/icons/earth.svg" alt="" className="h-6 w-6" />
                  </div>
                  <span className="flex-1 text-left text-[15px] text-black">
                    {tFields('international')}
                  </span>
                  <div
                    className={cn(
                      'flex h-5 w-5 items-center justify-center rounded-full border',
                      tripType === 'international'
                        ? 'border-[#242424] bg-[#242424]'
                        : 'border-[#f1f1f1]'
                    )}
                  >
                    {tripType === 'international' && (
                      <div className="h-2.5 w-2.5 rounded-full bg-white" />
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Initiator Dropdown */}
            <FormField
              control={form.control}
              name="initiator"
              render={({ field }) => {
                // Find the selected initiator's label for display (needed for virtualized select)
                const selectedInitiatorLabel =
                  employeeOptions.find((opt) => opt.value === field.value)?.label ?? null;

                return (
                  <FormItem>
                    <FormControl>
                      <div
                        className={cn(
                          'relative',
                          'aria-[invalid=true]:[&_button[data-field-border]]:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:hover:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:focus:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border][data-state=open]]:border-[#ff4d4f]'
                        )}
                      >
                        <Select onValueChange={field.onChange} value={field.value}>
                          <SelectTrigger
                            variant="formField"
                            showIcon={false}
                            data-field-border
                            disabled={isInitiatorLocked}
                            className={cn(
                              'h-14 rounded-2xl border border-[#e4e4e4] bg-white px-4 py-2 transition-colors focus:border-[#292929] data-[state=open]:border-[#292929]',
                              isInitiatorLocked
                                ? 'cursor-not-allowed bg-[#f9f9f9] text-[#666666]'
                                : 'hover:border-[#292929]'
                            )}
                          >
                            <div className="relative flex h-full w-full items-start justify-between">
                              <div className="relative flex h-full flex-1 items-center">
                                <FloatingSelectValue
                                  label={tFields('initiator')}
                                  hasValue={Boolean(field.value)}
                                >
                                  {selectedInitiatorLabel}
                                </FloatingSelectValue>
                              </div>
                              <img
                                src="/assets/icons/chevron-down.svg"
                                alt=""
                                className="mt-3 h-6 w-6"
                              />
                            </div>
                          </SelectTrigger>
                          {employeesLoading ? (
                            <SelectContent className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                              <div className="flex items-center justify-center py-4">
                                <Spinner variant="circle" />
                              </div>
                            </SelectContent>
                          ) : employeesError ? (
                            <SelectContent className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                              <div className="px-4 py-3 text-sm text-red-600">
                                {employeesError.message}
                              </div>
                            </SelectContent>
                          ) : (
                            <VirtualizedSelectContent
                              options={employeeOptions}
                              estimatedItemHeight={52}
                              className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]"
                              itemClassName="rounded-xl px-4 py-3 text-base leading-6 text-[#292929] hover:bg-[#f9f9f9] focus:bg-[#f9f9f9] focus:text-[#292929] data-[state=checked]:bg-[#f9f9f9] data-[state=checked]:text-[#292929] [&>span.absolute]:hidden"
                            />
                          )}
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* Cost Centre - Read-only field */}
            <div className="h-14 rounded-2xl border border-[#e4e4e4] bg-[#f9f9f9] px-4 py-2">
              <div className="relative flex h-full w-full items-start justify-between">
                <div className="relative flex h-full flex-1 items-center">
                  <div className="relative w-full">
                    <div
                      className={cn(
                        'pointer-events-none absolute left-0 origin-left text-[#666666] transition-all duration-200',
                        costCentreValue
                          ? 'top-0 text-[0.8125rem] leading-4'
                          : 'top-1/2 -translate-y-1/2 text-base leading-6'
                      )}
                    >
                      {tFields('costCentre')}
                    </div>
                    {costCentreValue && (
                      <div className="flex w-full min-w-0 items-start pt-5 text-base leading-6 text-[#999999]">
                        <span className="truncate">{costCentreValue}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Date Pickers - Improved with Georgian locale */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-start">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="space-y-2 self-start">
                    <FormLabel className="px-1 text-sm font-medium text-[#292929]">
                      {tFields('startDate')}
                    </FormLabel>
                    {isStartDateLocked ? (
                      <FormControl>
                        <div
                          className={cn(
                            'flex h-14 cursor-not-allowed items-center justify-between rounded-2xl border border-[#ebebeb] bg-[#f9f9f9] px-4 py-3 text-[#666666]',
                            'aria-[invalid=true]:border-[#ff4d4f]'
                          )}
                          aria-readonly="true"
                        >
                          <div className="flex items-center gap-3">
                            <img src="/assets/icons/calendar.svg" alt="" className="h-6 w-6" />
                            <span className="text-base text-[#292929]">
                              {field.value ? format(field.value, 'dd/MM/yyyy') : t('selectDate')}
                            </span>
                          </div>
                          <img src="/assets/icons/chevron-down.svg" alt="" className="h-6 w-6" />
                        </div>
                      </FormControl>
                    ) : (
                      <Popover open={isStartDateOpen} onOpenChange={setIsStartDateOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <div
                              className={cn(
                                'flex h-14 cursor-pointer items-center justify-between rounded-2xl border border-[#ebebeb] px-4 py-3 transition-colors focus-within:border-[#292929] hover:border-[#292929]',
                                'aria-[invalid=true]:border-[#ff4d4f]',
                                'aria-[invalid=true]:hover:border-[#ff4d4f]',
                                'aria-[invalid=true]:focus-within:border-[#ff4d4f]'
                              )}
                            >
                              <div className="flex items-center gap-3">
                                <img src="/assets/icons/calendar.svg" alt="" className="h-6 w-6" />
                                <span className="text-base text-[#292929]">
                                  {field.value
                                    ? format(field.value, 'dd/MM/yyyy')
                                    : t('selectDate')}
                                </span>
                              </div>
                              <img
                                src="/assets/icons/chevron-down.svg"
                                alt=""
                                className="h-6 w-6"
                              />
                            </div>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value ?? undefined}
                            onSelect={(date) => {
                              field.onChange(date ?? null);
                              setIsStartDateOpen(false);
                            }}
                            locale={ka}
                            captionLayout="dropdown"
                            className="rounded-2xl border-[#ebebeb]"
                            defaultMonth={field.value ?? today}
                            disabled={disableStartDates}
                          />
                        </PopoverContent>
                      </Popover>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="space-y-2 self-start">
                    <FormLabel className="px-1 text-sm font-medium text-[#292929]">
                      {tFields('endDate')}
                    </FormLabel>
                    <Popover open={isEndDateOpen} onOpenChange={setIsEndDateOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <div
                            className={cn(
                              'flex h-14 cursor-pointer items-center justify-between rounded-2xl border border-[#ebebeb] px-4 py-3 transition-colors focus-within:border-[#292929] hover:border-[#292929]',
                              'aria-[invalid=true]:border-[#ff4d4f]',
                              'aria-[invalid=true]:hover:border-[#ff4d4f]',
                              'aria-[invalid=true]:focus-within:border-[#ff4d4f]'
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <img src="/assets/icons/calendar.svg" alt="" className="h-6 w-6" />
                              <span className="text-base text-[#292929]">
                                {field.value ? format(field.value, 'dd/MM/yyyy') : t('selectDate')}
                              </span>
                            </div>
                            <img src="/assets/icons/chevron-down.svg" alt="" className="h-6 w-6" />
                          </div>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value ?? undefined}
                          onSelect={(date) => {
                            field.onChange(date ?? null);
                            setIsEndDateOpen(false);
                          }}
                          locale={ka}
                          captionLayout="dropdown"
                          className="rounded-2xl border-[#ebebeb]"
                          defaultMonth={field.value ?? startDateValue ?? today}
                          disabled={disableEndDates}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Acting Employee Field */}
            <FormField
              control={form.control}
              name="actingEmployee"
              render={({ field }) => {
                // Use substitute employees list when dates are available, otherwise fall back to regular employees
                const hasDateRange = Boolean(startDateString && endDateString);
                const employeeList = hasDateRange ? substituteEmployees : employees;
                const isLoadingEmployees = hasDateRange
                  ? substituteEmployeesLoading
                  : employeesLoading;
                const employeeListError = hasDateRange ? substituteEmployeesError : employeesError;

                // Convert employee list to virtualized options format
                const employeeOptions: VirtualizedSelectOption[] = (employeeList ?? []).map(
                  (employee) => {
                    // Check if this is a SubstituteEmployee with occupied days
                    const occupiedDays =
                      'occupiedDaysCount' in employee &&
                      typeof employee.occupiedDaysCount === 'number' &&
                      employee.occupiedDaysCount > 0
                        ? employee.occupiedDaysCount
                        : null;

                    return {
                      value: employee.id,
                      label: employee.name,
                      searchText: employee.name,
                      labelSuffix: occupiedDays
                        ? tFields('actingEmployeeOccupiedDropdown', { count: occupiedDays })
                        : undefined,
                    };
                  }
                );

                // Find the selected employee's name for display (show only name, not suffix, since warning is shown below)
                // Use initialActingEmployeeName as fallback when employee list isn't loaded yet
                const selectedEmployeeName =
                  (employeeList ?? []).find((emp) => emp.id === field.value)?.name ??
                  (field.value && initialActingEmployeeName ? initialActingEmployeeName : null);

                return (
                  <FormItem>
                    <FormControl>
                      <div
                        className={cn(
                          'relative',
                          'aria-[invalid=true]:[&_button[data-field-border]]:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:hover:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:focus:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border][data-state=open]]:border-[#ff4d4f]'
                        )}
                      >
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={!hasDateRange}
                          onOpenChange={(open) => {
                            if (open && hasDateRange) {
                              setApprovedFetchDates({ start: startDateString, end: endDateString });
                            }
                          }}
                        >
                          <SelectTrigger
                            variant="formField"
                            showIcon={false}
                            data-field-border
                            disabled={!hasDateRange}
                            className="h-14 rounded-2xl border border-[#e4e4e4] bg-white px-4 py-2 transition-colors hover:border-[#292929] focus:border-[#292929] disabled:cursor-not-allowed disabled:opacity-50 data-[state=open]:border-[#292929]"
                          >
                            <div className="relative flex h-full w-full items-start justify-between">
                              <div className="relative flex h-full flex-1 items-center">
                                <FloatingSelectValue
                                  label={tFields('actingEmployee')}
                                  hasValue={Boolean(field.value)}
                                >
                                  {selectedEmployeeName}
                                </FloatingSelectValue>
                              </div>
                              {field.value && hasDateRange ? (
                                <button
                                  type="button"
                                  aria-label={tCommon('clear')}
                                  title={tCommon('clear')}
                                  className="text-[#292929] hover:bg-[#f1f1f1] hover:text-[#1f1f1f] focus-visible:ring-ring/50 focus-visible:outline-none focus-visible:ring-[3px] self-center rounded-full p-1 transition-colors"
                                  onPointerDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                  }}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    field.onChange('');
                                  }}
                                >
                                  <X className="h-[18px] w-[18px] stroke-[2.25]" />
                                </button>
                              ) : (
                                <img
                                  src="/assets/icons/chevron-down.svg"
                                  alt=""
                                  className="mt-3 h-6 w-6"
                                />
                              )}
                            </div>
                          </SelectTrigger>
                          {isLoadingEmployees ? (
                            <SelectContent className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                              <div className="flex items-center justify-center py-4">
                                <Spinner variant="circle" />
                              </div>
                            </SelectContent>
                          ) : employeeListError ? (
                            <SelectContent className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                              <div className="px-4 py-3 text-sm text-red-600">
                                {employeeListError.message}
                              </div>
                            </SelectContent>
                          ) : (
                            <VirtualizedSelectContent
                              options={employeeOptions}
                              estimatedItemHeight={52}
                              className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]"
                              itemClassName="rounded-xl px-4 py-3 text-base leading-6 text-[#292929] hover:bg-[#f9f9f9] focus:bg-[#f9f9f9] focus:text-[#292929] data-[state=checked]:bg-[#f9f9f9] data-[state=checked]:text-[#292929] [&>span.absolute]:hidden"
                            />
                          )}
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage />
                    {/* Warning for substitute employee with occupied days */}
                    {substituteHasOccupiedDays && selectedSubstituteEmployee && (
                      <div className="mt-2 flex items-start gap-2 rounded-xl bg-[#fff8e6] px-3 py-2.5 text-sm text-[#946800]">
                        <img
                          src="/assets/icons/error-circle.svg"
                          alt=""
                          className="mt-0.5 h-4 w-4 flex-shrink-0"
                          style={{
                            filter:
                              'brightness(0) saturate(100%) invert(45%) sepia(94%) saturate(456%) hue-rotate(7deg) brightness(95%) contrast(89%)',
                          }}
                        />
                        <div className="flex flex-col gap-0.5">
                          <span>
                            {tFields('actingEmployeeOccupiedWarning', {
                              count: selectedSubstituteEmployee.occupiedDaysCount,
                            })}
                          </span>
                          {selectedSubstituteEmployee.occupiedRanges &&
                            selectedSubstituteEmployee.occupiedRanges.length > 0 && (
                              <span className="text-[13px]">
                                {tFields('actingEmployeeOccupiedRanges', {
                                  ranges: formatOccupiedRanges(selectedSubstituteEmployee),
                                })}
                              </span>
                            )}
                        </div>
                      </div>
                    )}
                  </FormItem>
                );
              }}
            />

            {/* Permanent residence country */}
            <FormField
              control={form.control}
              name="permanentResidenceCountry"
              render={({ field }) => {
                // Find the selected country's label for display (needed for virtualized select)
                const selectedCountryLabel =
                  countryOptions.find((opt) => opt.value === field.value)?.label ?? null;

                return (
                  <FormItem>
                    <FormControl>
                      <div
                        className={cn(
                          'relative',
                          'aria-[invalid=true]:[&_button[data-field-border]]:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:hover:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:focus:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border][data-state=open]]:border-[#ff4d4f]'
                        )}
                      >
                        <Select
                          onValueChange={(value) => {
                            field.onChange(value);
                          }}
                          value={field.value}
                        >
                          <SelectTrigger
                            variant="formField"
                            showIcon={false}
                            data-field-border
                            className="h-14 rounded-2xl border border-[#e4e4e4] bg-white px-4 py-2 transition-colors hover:border-[#292929] focus:border-[#292929] data-[state=open]:border-[#292929]"
                          >
                            <div className="relative flex h-full w-full items-start justify-between">
                              <div className="relative flex h-full flex-1 items-center">
                                <FloatingSelectValue
                                  label={tFields('permanentResidenceCountry')}
                                  hasValue={Boolean(field.value)}
                                >
                                  {selectedCountryLabel}
                                </FloatingSelectValue>
                              </div>
                              <img
                                src="/assets/icons/chevron-down.svg"
                                alt=""
                                className="mt-3 h-6 w-6"
                              />
                            </div>
                          </SelectTrigger>
                          {countriesLoading ? (
                            <SelectContent className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                              <div className="flex items-center justify-center py-4">
                                <Spinner variant="circle" />
                              </div>
                            </SelectContent>
                          ) : countriesError ? (
                            <SelectContent className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                              <div className="px-4 py-3 text-sm text-red-600">
                                {countriesError.message}
                              </div>
                            </SelectContent>
                          ) : (
                            <VirtualizedSelectContent
                              options={countryOptions}
                              estimatedItemHeight={52}
                              className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]"
                              itemClassName="rounded-xl px-4 py-3 text-base leading-6 text-[#292929] hover:bg-[#f9f9f9] focus:bg-[#f9f9f9] focus:text-[#292929] data-[state=checked]:bg-[#f9f9f9] data-[state=checked]:text-[#292929] [&>span.absolute]:hidden"
                            />
                          )}
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* Permanent residence city - Virtualized for performance with large lists */}
            <FormField
              control={form.control}
              name="permanentResidenceCity"
              render={({ field }) => {
                // Find the selected city's label for display (needed for virtualized select)
                const selectedCityLabel =
                  residenceCityOptions.find((opt) => opt.value === field.value)?.label ?? null;

                return (
                  <FormItem>
                    <FormControl>
                      <div
                        className={cn(
                          'relative',
                          'aria-[invalid=true]:[&_button[data-field-border]]:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:hover:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border]]:focus:border-[#ff4d4f]',
                          'aria-[invalid=true]:[&_button[data-field-border][data-state=open]]:border-[#ff4d4f]'
                        )}
                      >
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isResidenceCityDisabled}
                        >
                          <SelectTrigger
                            variant="formField"
                            showIcon={false}
                            data-field-border
                            disabled={isResidenceCityDisabled}
                            className="h-14 rounded-2xl border border-[#e4e4e4] bg-white px-4 py-2 transition-colors hover:border-[#292929] focus:border-[#292929] disabled:cursor-not-allowed disabled:border-[#f1f1f1] disabled:bg-[#f7f7f7] data-[state=open]:border-[#292929]"
                          >
                            <div className="relative flex h-full w-full items-start justify-between">
                              <div className="relative flex h-full flex-1 items-center">
                                <FloatingSelectValue
                                  label={tFields('permanentResidenceCity')}
                                  hasValue={Boolean(field.value)}
                                  disabled={isResidenceCityDisabled}
                                >
                                  {selectedCityLabel}
                                </FloatingSelectValue>
                              </div>
                              <img
                                src="/assets/icons/chevron-down.svg"
                                alt=""
                                className="mt-3 h-6 w-6"
                              />
                            </div>
                          </SelectTrigger>
                          {citiesLoading ? (
                            <SelectContent className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                              <div className="flex items-center justify-center py-4">
                                <Spinner variant="circle" />
                              </div>
                            </SelectContent>
                          ) : citiesError ? (
                            <SelectContent className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]">
                              <div className="px-4 py-3 text-sm text-red-600">
                                {citiesError.message}
                              </div>
                            </SelectContent>
                          ) : (
                            <VirtualizedSelectContent
                              options={residenceCityOptions}
                              estimatedItemHeight={52}
                              className="rounded-2xl border border-[#e4e4e4] bg-white p-1 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.08)]"
                              itemClassName="rounded-xl px-4 py-3 text-base leading-6 text-[#292929] hover:bg-[#f9f9f9] focus:bg-[#f9f9f9] focus:text-[#292929] data-[state=checked]:bg-[#f9f9f9] data-[state=checked]:text-[#292929] [&>span.absolute]:hidden"
                            />
                          )}
                        </Select>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* Purpose with floating label */}
            <FormField
              control={form.control}
              name="purpose"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div
                      className={cn(
                        'h-14 rounded-2xl border border-[#e4e4e4] bg-white px-4 py-2 transition-colors focus-within:border-[#292929] hover:border-[#292929]',
                        'aria-[invalid=true]:border-[#ff4d4f]',
                        'aria-[invalid=true]:hover:border-[#ff4d4f]',
                        'aria-[invalid=true]:focus-within:border-[#ff4d4f]'
                      )}
                    >
                      <div className="relative flex h-full w-full items-start justify-between">
                        <div className="relative flex h-full flex-1 items-center">
                          <div className="relative w-full">
                            <input
                              id="purpose"
                              {...field}
                              value={field.value || ''}
                              placeholder=" "
                              className="peer w-full border-none bg-transparent p-0 pt-5 text-base leading-6 text-[#292929] outline-none"
                            />
                            <label
                              htmlFor="purpose"
                              className="pointer-events-none absolute left-0 origin-left text-[#666666] transition-all duration-200 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-base peer-placeholder-shown:leading-6 peer-focus:top-0 peer-focus:translate-y-0 peer-focus:text-[0.8125rem] peer-focus:leading-4 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:translate-y-0 peer-[:not(:placeholder-shown)]:text-[0.8125rem] peer-[:not(:placeholder-shown)]:leading-4"
                            >
                              {tFields('purpose')}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="min-h-[5.6875rem] rounded-2xl border border-[#ebebeb] px-4 py-3">
              <FormField
                control={form.control}
                name="additionalComment"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <div className="relative w-full">
                        <textarea
                          id="additionalComment"
                          placeholder=" "
                          className="peer min-h-[4.5rem] w-full resize-none border-0 bg-transparent p-0 pt-5 text-base text-[#292929] focus:outline-none"
                          {...field}
                          value={field.value || ''}
                        />
                        <label
                          htmlFor="additionalComment"
                          className="pointer-events-none absolute left-0 origin-left text-[#666666] transition-all duration-200 peer-placeholder-shown:text-base peer-placeholder-shown:leading-6 peer-focus:top-0 peer-focus:text-[0.8125rem] peer-focus:leading-4 peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:text-[0.8125rem] peer-[:not(:placeholder-shown)]:leading-4"
                        >
                          {tFields('additionalComment')}
                        </label>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* Trip Expenses */}
            <TripExpensesSection form={form} tExpenses={tExpenses} tCommon={tCommon} />
          </div>

          {/* Save and Cancel buttons */}
          <div className="mt-4">
            <div className="flex flex-col gap-2 rounded-3xl bg-white p-3 shadow-[0px_-8px_24px_-8px_rgba(31,31,31,0.12)] sm:flex-row sm:items-center sm:justify-end sm:bg-transparent sm:p-0 sm:shadow-none">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancel}
                className="h-12 w-full cursor-pointer rounded-full bg-white px-4 py-3 text-base font-medium text-black hover:bg-[#f9f9f9] sm:w-auto"
              >
                {tActions('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isBusy}
                className="h-12 w-full cursor-pointer rounded-full bg-[#242424] px-4 py-3 text-base font-medium text-white hover:bg-[#1a1a1a] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {isBusy ? (
                  <div className="flex items-center gap-2">
                    <Spinner variant="circle" className="h-4 w-4" />
                    <span>{tCommon('loading')}</span>
                  </div>
                ) : (
                  tCommon('save')
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>

      {/* Confirmation dialog for substitute employee with occupied days */}
      <ConfirmationDialog
        isOpen={showSubstituteWarningDialog}
        onClose={handleCancelSubstituteWarning}
        onConfirm={handleConfirmSubstituteWarning}
        title={tTrips('actingEmployeeOccupiedConfirmation.title')}
        message={tTrips('actingEmployeeOccupiedConfirmation.message', {
          count: selectedSubstituteEmployee?.occupiedDaysCount ?? 0,
          ranges: formatOccupiedRanges(selectedSubstituteEmployee),
        })}
        confirmText={tTrips('actingEmployeeOccupiedConfirmation.confirm')}
        cancelText={tTrips('actingEmployeeOccupiedConfirmation.cancel')}
        confirmButtonVariant="primary"
      />
    </div>
  );
}

export default TripForm;
